import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import type { LevelMode, LogFilters, LogLevel, TimeRange } from "@/lib/types";
import { EMPTY_FILTERS, LEVELS, TIME_RANGES } from "@/lib/types";

const KEY_MAP: Record<keyof LogFilters, string> = {
  level: "level",
  levelMode: "levelMode",
  client: "client",
  service: "service",
  host: "host",
  traceId: "trace",
  search: "q",
  limit: "limit",
  range: "range",
  from: "from",
  to: "to",
};

function parseFilters(params: URLSearchParams): LogFilters {
  const out: LogFilters = { ...EMPTY_FILTERS };
  const level = params.get(KEY_MAP.level);
  if (level && LEVELS.includes(level.toUpperCase() as LogLevel)) {
    out.level = level.toUpperCase() as LogLevel;
  }
  const levelMode = params.get(KEY_MAP.levelMode);
  if (levelMode === "exact" || levelMode === "minimum") out.levelMode = levelMode as LevelMode;
  const client = params.get(KEY_MAP.client);
  if (client) out.client = client;
  const service = params.get(KEY_MAP.service);
  if (service) out.service = service;
  const host = params.get(KEY_MAP.host);
  if (host) out.host = host;
  // Legacy-Alias: das alte Dashboard verwendete `trace_id` und `search` —
  // wir akzeptieren beide, schreiben aber immer die neuen Schlüssel.
  const traceId = params.get(KEY_MAP.traceId) ?? params.get("trace_id");
  if (traceId) out.traceId = traceId;
  const search = params.get(KEY_MAP.search) ?? params.get("search");
  if (search) out.search = search;
  const limit = params.get(KEY_MAP.limit);
  if (limit) {
    const n = parseInt(limit, 10);
    if (Number.isFinite(n) && n > 0 && n <= 5000) out.limit = n;
  }
  const range = params.get(KEY_MAP.range);
  if (range && TIME_RANGES.includes(range as TimeRange)) out.range = range as TimeRange;
  const from = params.get(KEY_MAP.from);
  if (from) out.from = from;
  const to = params.get(KEY_MAP.to);
  if (to) out.to = to;
  return out;
}

/**
 * Kanonischer Filter→URL-Builder. Überall verwenden, wo Filter in Query-
 * Params übersetzt werden (Logs-Seite, Sidebar-Sichten, CommandPalette) —
 * lokale Kopien laufen bei neuen Filter-Feldern auseinander.
 */
export function writeFilters(filters: LogFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.level) params.set(KEY_MAP.level, filters.level);
  if (filters.levelMode && filters.levelMode !== "minimum") params.set(KEY_MAP.levelMode, filters.levelMode);
  if (filters.client) params.set(KEY_MAP.client, filters.client);
  if (filters.service) params.set(KEY_MAP.service, filters.service);
  if (filters.host) params.set(KEY_MAP.host, filters.host);
  if (filters.traceId) params.set(KEY_MAP.traceId, filters.traceId);
  if (filters.search) params.set(KEY_MAP.search, filters.search);
  if (filters.limit && filters.limit !== 100) params.set(KEY_MAP.limit, String(filters.limit));
  if (filters.range) params.set(KEY_MAP.range, filters.range);
  // from/to nur bei "custom" — bei Presets wären sie verwirrende Altlasten.
  if (filters.range === "custom") {
    if (filters.from) params.set(KEY_MAP.from, filters.from);
    if (filters.to) params.set(KEY_MAP.to, filters.to);
  }
  return params;
}

export function useUrlFilters() {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => parseFilters(params), [params]);

  const setFilters = useCallback(
    (next: LogFilters) => {
      setParams(writeFilters(next), { replace: true });
    },
    [setParams],
  );

  const patchFilters = useCallback(
    (patch: Partial<LogFilters>) => {
      setParams(writeFilters({ ...filters, ...patch }), { replace: true });
    },
    [filters, setParams],
  );

  const resetFilters = useCallback(() => {
    setParams(new URLSearchParams(), { replace: true });
  }, [setParams]);

  return { filters, setFilters, patchFilters, resetFilters };
}
